import { Code, ConnectError } from '@connectrpc/connect'
import { isSessionReplaced } from '@/api/errors'
import type { AuthResponse } from '@/gen/player/v1/player_pb'

/** Why a session ended, when the player should be told. */
export type SessionEndReason = 'session_replaced'

/** Proactive refresh cadence. Access tokens live ~1 h; rotate before that. */
export const PROACTIVE_REFRESH_MS = 50 * 60 * 1000

export interface RefresherDeps {
  getTokens: () => { accessToken: string | null; refreshToken: string | null }
  /** WebSession.Refresh. */
  refreshRpc: (refreshToken: string) => Promise<AuthResponse>
  /** Firebase getIdToken(true) → WebSession.SignIn; null when there is no Firebase user. */
  reauth: () => Promise<AuthResponse | null>
  apply: (res: AuthResponse) => void
  /** The session is definitively gone: sign out locally and go to /login. */
  fail: (reason?: SessionEndReason) => void | Promise<void>
}

/**
 * "Nothing answered" versus "the server said no". Only the second is evidence
 * that a session is dead; a dropped connection must never sign anyone out
 * (the Unity BootGate lesson: a transport failure that clears a good token
 * strands the player on a login form that cannot work either).
 */
export function isTransportFailure(err: unknown): boolean {
  if (err instanceof ConnectError) {
    return (
      err.code === Code.Unavailable ||
      err.code === Code.DeadlineExceeded ||
      err.code === Code.Canceled ||
      err.code === Code.ResourceExhausted
    )
  }
  return err instanceof TypeError
}

class TransportFailure extends Error {}

/**
 * Single-flight token refresh: however many calls fail with Unauthenticated
 * at once, ONE Refresh goes out and every caller awaits it.
 *
 * Order: WebSession.Refresh(refresh token) → Firebase re-auth → sign out.
 *
 * EXCEPT when Refresh answers session_replaced: the player signed in on
 * another browser, and only one is allowed. Re-auth would be a sign-in, which
 * would take the session back and send the other browser through this same
 * path — two tabs signing each other out forever. So that one signs out and
 * says why; signing in here again is the player's call, not ours.
 */
export function createRefresher(deps: RefresherDeps) {
  let inflight: Promise<string | null> | null = null

  async function run(): Promise<string | null> {
    try {
      const { refreshToken } = deps.getTokens()
      if (refreshToken) {
        try {
          const res = await deps.refreshRpc(refreshToken)
          deps.apply(res)
          return res.accessToken
        } catch (err) {
          if (isTransportFailure(err)) throw new TransportFailure()
          if (isSessionReplaced(err)) {
            await deps.fail('session_replaced')
            return null
          }
          // Refused (expired / revoked): fall through to Firebase.
        }
      }
      try {
        const res = await deps.reauth()
        if (res) {
          deps.apply(res)
          return res.accessToken
        }
      } catch (err) {
        if (isTransportFailure(err)) throw new TransportFailure()
      }
      await deps.fail()
      return null
    } catch (err) {
      if (err instanceof TransportFailure) return null
      throw err
    }
  }

  return {
    /**
     * @param usedToken the access token the failed call carried. If the
     *   session has moved on since (another refresh landed), the caller just
     *   retries with the current token.
     * @param opts.force rotate even if the token looks current (the proactive timer).
     */
    refresh(usedToken: string | null, opts?: { force?: boolean }): Promise<string | null> {
      const current = deps.getTokens().accessToken
      if (!opts?.force && current && usedToken && current !== usedToken) {
        return Promise.resolve(current)
      }
      if (!inflight) {
        inflight = run().finally(() => {
          inflight = null
        })
      }
      return inflight
    },
    isRefreshing(): boolean {
      return inflight !== null
    },
  }
}

export type Refresher = ReturnType<typeof createRefresher>
